// lib/nav/derive-nav-visibility.ts
//
// Pure domain layer (hex: .tondocs/skills/hex.md) for the mobile bottom-nav
// bar's two decisions — "should the bar be hidden right now" and "which
// section should light up" — previously an inline ≥7-boolean OR-chain plus
// two hand-rolled if-ladders living directly in
// components/UserUI/Navigation/bottom-nav-bar-client.tsx (spec §B.1's
// canonical boolean-flag tangle). Zero React imports; unit-tested without a
// renderer (tests/unit/derive-nav-visibility.test.ts).
//
// @see .tondocs/feed/OVERLAY_FSM_SPEC.md §E row 1.7 (this migration)
// @see .tondocs/feed/OVERLAY_FSM_SPEC.md §C.2 ("derived selectors replace the boolean tangle")
// @see .tondocs/skills/react-state-design.md (boolean-tangle → derived value)
// @see lib/overlay/overlay-state.ts (OverlayState this reads)

import type { OverlayState } from "@/lib/overlay/overlay-state";

export type NavSection = "home" | "search" | "orders" | "profile" | "create";

export interface DeriveHideNavBarInput {
  overlay: OverlayState;
  pathname: string | null;
  isMobileViewport: boolean;
  isSelectionModeActive: boolean;
}

/**
 * Should the mobile bottom-nav bar be hidden right now?
 *
 * One condition per row — every row here is a 1:1 port of a term from the
 * pre-1.7 OR-chain, kept in the SAME order for auditability:
 *
 * | # | Condition | Source (pre-1.7) |
 * |---|---|---|
 * | 1 | selection mode active (bulk-select on a listings grid) | `useSelectionMode()` |
 * | 2 | chat panel open | `usePanelIs("chat")` |
 * | 3 | on the `/chat` route | `pathname?.startsWith("/chat")` |
 * | 4 | profile-hub panel open | `usePanelIs("profile")` |
 * | 5 | inline mobile profile-edit panel open | was `dataset.profileEditOpen` (mechanism 4, §B.1) — migrated to the `profile-edit` panel kind THIS step |
 * | 6 | snap-feed details panel open, AND on `/feed`, AND mobile viewport (desktop 420px panel never hid the nav — step 1.5 finding) | `usePanelIs("feed-details")` + `pathname === "/feed"` + `useMobile()` |
 * | 7 | on the `/profile/edit` ROUTE (the full-page editor, unrelated to #5's in-place mode) | `pathname === "/profile/edit"` |
 *
 * No behavior change from pre-1.7: same seven conditions, same OR, same
 * order. Only the *shape* changed (inline booleans → typed inputs read off
 * `OverlayState` + pathname).
 */
export function deriveHideNavBar(input: DeriveHideNavBarInput): boolean {
  const { overlay, pathname, isMobileViewport, isSelectionModeActive } = input;

  const isChatPanelOpen = overlay.panel.kind === "chat";
  const isProfilePanelOpen = overlay.panel.kind === "profile";
  const isProfileEditPanelOpen = overlay.panel.kind === "profile-edit";
  const isFeedDetailsPanelOpen = overlay.panel.kind === "feed-details";

  return (
    isSelectionModeActive ||
    isChatPanelOpen ||
    pathname?.startsWith("/chat") === true ||
    isProfilePanelOpen ||
    isProfileEditPanelOpen ||
    (pathname === "/feed" && isMobileViewport && isFeedDetailsPanelOpen) ||
    pathname === "/profile/edit"
  );
}

export interface DeriveActiveSectionInput {
  overlay: OverlayState;
  pathname: string | null;
  /** Own username (logged-in user), for the "viewing my own /[username]" case. */
  myUsername: string | null;
  /** Transient click-override (component's own `useState`, reset on pathname change). */
  clickedSection: NavSection | null;
}

/**
 * Which nav section should be highlighted, for a LOGGED-IN user. 1:1 port of
 * the pre-1.7 `getActiveSection()` — same precedence order, same routes.
 */
export function deriveActiveSection(input: DeriveActiveSectionInput): NavSection | null {
  const { overlay, pathname, myUsername, clickedSection } = input;

  if (clickedSection) return clickedSection;

  if (overlay.panel.kind === "creation") return "create";
  if (overlay.panel.kind === "profile") return "profile";

  if (pathname === "/feed") return "home";
  if (pathname?.startsWith("/search")) return "search";
  if (
    pathname?.startsWith("/orders") ||
    pathname?.startsWith("/offers") ||
    pathname?.startsWith("/my-listings") ||
    pathname?.startsWith("/shipping-settings") ||
    pathname?.startsWith("/analytics")
  ) {
    return "orders";
  }
  if (myUsername && pathname === `/${myUsername}`) return "profile";
  if (
    pathname?.startsWith("/profile") ||
    pathname?.startsWith("/settings") ||
    pathname === "/account" ||
    pathname?.startsWith("/saved") ||
    pathname?.startsWith("/changelog") ||
    pathname?.startsWith("/contact")
  ) {
    return "profile";
  }

  return null;
}

export interface DeriveGuestActiveSectionInput {
  pathname: string | null;
  /** Transient click-override (component's own `useState`, reset on pathname change). */
  clickedSection: "home" | null;
}

/**
 * Which nav section should be highlighted, for a GUEST (logged-out) user.
 * 1:1 port of the pre-1.7 `getGuestActiveSection()` — deliberately does NOT
 * read `overlay` or `myUsername`: the guest nav never had a "create"/"my
 * profile" affordance to light up, so this stays a smaller, separate
 * decision rather than being folded into `deriveActiveSection` behind an
 * `isAuthenticated` flag (which would have made it possible for an overlay
 * panel meant for logged-in users to leak into the guest section by
 * accident).
 */
export function deriveGuestActiveSection(input: DeriveGuestActiveSectionInput): "home" | null {
  const { pathname, clickedSection } = input;

  if (clickedSection) return clickedSection;
  if (pathname === "/") return "home";
  if (pathname === "/feed") return "home";
  return null;
}
